import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useServices } from '../../context/ServiceContext';
import { useLoading } from '../../context/LoadingContext';
import { useRoutePath } from '../../utils/getRoutePath';
import Breadcrumb from '../common/Breadcrumb';
import {
  CalendarIcon,
  ArrowLeftIcon,
  PencilIcon,
  CheckCircleIcon,
  XCircleIcon,
  CurrencyDollarIcon,
  CubeIcon,
  GlobeAltIcon,
  PhotoIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

const ServiceView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const getRoutePath = useRoutePath();
  const { hideLoading } = useLoading();
  const { services, getServiceById } = useServices();

  const [service, setService] = useState(null);
  const [isFetching, setIsFetching] = useState(true);
  const hasFetchedService = useRef(false);

  const fetchService = useCallback(async () => {
    if (!id || hasFetchedService.current) return;
    hasFetchedService.current = true;
    setIsFetching(true);

    try {
      // Always use getServiceById for full detail (includes rates, products, portal_options)
      const response = await getServiceById(id);
      if (response?.status === 200 && response?.data) {
        setService(response.data);
      } else {
        setService(null);
      }
    } catch (error) {
      console.error('Error fetching service data:', error);
      setService(null);
    } finally {
      setIsFetching(false);
      hideLoading();
    }
  }, [id, getServiceById, hideLoading]);

  useEffect(() => {
    // Quick resolve from context list while full fetch loads
    if (id && services?.data?.length && !service) {
      const existing = services.data.find(
        (item) => Number(item.id) === Number(id)
      );
      if (existing && !hasFetchedService.current) {
        setService(existing);
        // Don't stop fetching – the list item may not have full details
      }
    }
    fetchService();
  }, [id, services, service, fetchService]);

  const handleEdit = () => {
    navigate(getRoutePath(`/services/edit/${id}`));
  };

  /* ─── helpers ────────────────────────────────────────── */

  const getStatusBadge = () => {
    const isActive =
      service?.active === 1 || service?.active === true ||
      service?.status === 1 || service?.status === true;
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
        {isActive ? (<><CheckCircleIcon className="h-4 w-4 mr-1" />Active</>) : (<><XCircleIcon className="h-4 w-4 mr-1" />Inactive</>)}
      </span>
    );
  };

  const fmt = (v) => {
    if (v === null || v === undefined || v === '') return 'N/A';
    return v;
  };

  const fmtCurrency = (v) => {
    const n = parseFloat(v);
    if (isNaN(n)) return 'N/A';
    return `$${n.toFixed(2)}`;
  };

  const calculateTotal = (rate, qty) => {
    return ((parseFloat(rate) || 0) * (parseFloat(qty) || 0)).toFixed(2);
  };

  /* ─── loading / not-found states ─────────────────────── */

  if (!isFetching && !service) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Service Not Found</h2>
          <p className="text-gray-600 mb-6">The service you&apos;re looking for doesn&apos;t exist or has been removed.</p>
          <button onClick={() => navigate(getRoutePath('/services'))} className="inline-flex items-center px-4 py-2 bg-[#009efb] text-white rounded-lg hover:bg-[#0089db] transition-colors">
            <ArrowLeftIcon className="h-5 w-5 mr-2" />Back to Services
          </button>
        </div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-3">
          <div className="h-8 w-8 border-2 border-[#009efb] border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-600">Loading service information...</p>
        </div>
      </div>
    );
  }

  /* ─── derived data ───────────────────────────────────── */

  const title = service.name || service.title || 'Service Details';
  const categoryName = service.service_category?.name || 'N/A';
  const rates = Array.isArray(service.rates) ? service.rates : [];
  const products = Array.isArray(service.products) ? service.products : [];
  const portalOptions = service.portal_options || {};
  const displayImages = Array.isArray(service.display_images) ? service.display_images.filter(img => img.status !== 404) : [];

  // Totals helpers
  const calcRatesTotal = (field) => rates.reduce((sum, r) => sum + ((parseFloat(r[field]) || 0) * (parseFloat(r.qty_hrs) || 0)), 0);
  const calcProductsTotal = (field) => products.reduce((sum, p) => sum + ((parseFloat(p[field]) || 0) * (parseFloat(p.qty) || 0)), 0);

  const hasAdditionalRates = rates.some(r =>
    (parseFloat(r.addon_rate) || 0) > 0 ||
    (parseFloat(r.premium_rate) || 0) > 0 ||
    (parseFloat(r.value_rate) || 0) > 0 ||
    (parseFloat(r.after_hours_rate) || 0) > 0
  );

  /* ─── render ─────────────────────────────────────────── */

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6">

        {/* ─── Header ─── */}
        <div className="mb-8">
          <Breadcrumb />
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between mt-4 space-y-4 md:space-y-0">
            <div className="flex items-start md:items-center space-x-3">
              <div className="p-2 bg-[#009efb] rounded-lg">
                <CalendarIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center flex-wrap gap-2">
                  <span>{title}</span>
                </h1>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-gray-600">
                  <span>{getStatusBadge()}</span>
                  {categoryName !== 'N/A' && (
                    <span className="text-gray-500">Category: {categoryName}</span>
                  )}
                  {service.code && (
                    <span className="text-gray-500">Code: {service.code}</span>
                  )}
                  {service.formated_created_at && (
                    <span className="text-gray-500">Created: {service.formated_created_at}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center sm:flex-wrap gap-2 w-full md:w-auto">
              <button onClick={() => navigate(getRoutePath('/services'))} className="inline-flex items-center justify-center px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-md bg-white hover:bg-gray-50 transition-colors w-full sm:w-auto">
                <ArrowLeftIcon className="h-4 w-4 mr-2" />Back to List
              </button>
              <button onClick={handleEdit} className="inline-flex items-center justify-center px-4 py-2 text-sm bg-[#009efb] text-white rounded-md hover:bg-[#0089db] transition-colors w-full sm:w-auto">
                <PencilIcon className="h-4 w-4 mr-2" />Edit Service
              </button>
            </div>
          </div>
        </div>

        {/* ─── Main Grid ─── */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">

          {/* ─── Left Column ─── */}
          <div className="xl:col-span-2 space-y-6">

            {/* Basic Info Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-[#009efb] to-[#0089db] px-6 py-3">
                <div className="flex items-center">
                  <CalendarIcon className="h-5 w-5 text-white mr-3" />
                  <h2 className="text-xl font-semibold text-white">Service Information</h2>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Service Name</label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">{fmt(service.name)}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">{categoryName}</div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">{fmt(service.code)}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <div className="p-3 bg-gray-50 rounded-lg">{getStatusBadge()}</div>
                  </div>
                </div>
                {service.media_url && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Media URL</label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                      <a href={service.media_url} target="_blank" rel="noopener noreferrer" className="text-[#009efb] hover:text-[#0089db] break-all">
                        {service.media_url}
                      </a>
                    </div>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-900 min-h-[60px] whitespace-pre-wrap">
                    {service.description || 'No description provided.'}
                  </div>
                </div>
              </div>
            </div>

            {/* Rates Card */}
            {rates.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-[#009efb] to-[#0089db] px-6 py-3">
                  <div className="flex items-center">
                    <CurrencyDollarIcon className="h-5 w-5 text-white mr-3" />
                    <h2 className="text-xl font-semibold text-white">Rates ({rates.length})</h2>
                  </div>
                </div>
                <div className="p-6 space-y-6">
                  {rates.map((rate, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <h3 className="text-md font-semibold text-gray-900 mb-3">
                        {rate.rate_name || `Rate ${index + 1}`}
                      </h3>

                      {/* Core Rates */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-500">Qty/Hrs</label>
                          <div className="text-sm font-medium text-gray-900">{fmt(rate.qty_hrs)}</div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500">Regular Rate</label>
                          <div className="text-sm font-medium text-gray-900">{fmtCurrency(rate.regular_rate)}</div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500">Regular Total</label>
                          <div className="text-sm font-semibold text-gray-900">{fmtCurrency(calculateTotal(rate.regular_rate, rate.qty_hrs))}</div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500">Member Rate</label>
                          <div className="text-sm font-medium text-gray-900">{fmtCurrency(rate.member_rate)}</div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500">Member Total</label>
                          <div className="text-sm font-semibold text-gray-900">{fmtCurrency(calculateTotal(rate.member_rate, rate.qty_hrs))}</div>
                        </div>
                      </div>

                      {/* Additional Rates */}
                      {((parseFloat(rate.addon_rate) || 0) > 0 || (parseFloat(rate.premium_rate) || 0) > 0 || (parseFloat(rate.value_rate) || 0) > 0 || (parseFloat(rate.after_hours_rate) || 0) > 0) && (
                        <div className="border-t border-gray-100 pt-3 mb-3">
                          <p className="text-xs font-medium text-gray-500 mb-2 uppercase">Additional Rates</p>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {(parseFloat(rate.addon_rate) || 0) > 0 && (
                              <>
                                <div>
                                  <label className="block text-xs font-medium text-gray-500">Add-On Rate</label>
                                  <div className="text-sm text-gray-900">{fmtCurrency(rate.addon_rate)}</div>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-500">Add-On Total</label>
                                  <div className="text-sm text-gray-900">{fmtCurrency(calculateTotal(rate.addon_rate, rate.qty_hrs))}</div>
                                </div>
                              </>
                            )}
                            {(parseFloat(rate.premium_rate) || 0) > 0 && (
                              <>
                                <div>
                                  <label className="block text-xs font-medium text-gray-500">Premium Rate</label>
                                  <div className="text-sm text-gray-900">{fmtCurrency(rate.premium_rate)}</div>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-500">Premium Total</label>
                                  <div className="text-sm text-gray-900">{fmtCurrency(calculateTotal(rate.premium_rate, rate.qty_hrs))}</div>
                                </div>
                              </>
                            )}
                            {(parseFloat(rate.value_rate) || 0) > 0 && (
                              <>
                                <div>
                                  <label className="block text-xs font-medium text-gray-500">Value Rate</label>
                                  <div className="text-sm text-gray-900">{fmtCurrency(rate.value_rate)}</div>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-500">Value Total</label>
                                  <div className="text-sm text-gray-900">{fmtCurrency(calculateTotal(rate.value_rate, rate.qty_hrs))}</div>
                                </div>
                              </>
                            )}
                            {(parseFloat(rate.after_hours_rate) || 0) > 0 && (
                              <>
                                <div>
                                  <label className="block text-xs font-medium text-gray-500">Afterhours Rate</label>
                                  <div className="text-sm text-gray-900">{fmtCurrency(rate.after_hours_rate)}</div>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-500">Afterhours Total</label>
                                  <div className="text-sm text-gray-900">{fmtCurrency(calculateTotal(rate.after_hours_rate, rate.qty_hrs))}</div>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Commission */}
                      {rate.is_pays_commission && (
                        <div className="border-t border-gray-100 pt-3 mb-3">
                          <p className="text-xs font-medium text-gray-500 mb-2 uppercase">Commission</p>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-500">Fixed Commission</label>
                              <div className="text-sm text-gray-900">{fmtCurrency(rate.fixed_commission_amount)}</div>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-500">Percentage Commission</label>
                              <div className="text-sm text-gray-900">{rate.percentage_commission != null ? `${rate.percentage_commission}%` : 'N/A'}</div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Bonus */}
                      {rate.is_pays_bonus && (
                        <div className="border-t border-gray-100 pt-3 mb-3">
                          <p className="text-xs font-medium text-gray-500 mb-2 uppercase">Bonus</p>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-500">Fixed Bonus</label>
                              <div className="text-sm text-gray-900">{fmtCurrency(rate.fixed_bonus_amount)}</div>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-500">Percentage Bonus</label>
                              <div className="text-sm text-gray-900">{rate.percentage_bonus != null ? `${rate.percentage_bonus}%` : 'N/A'}</div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Internal Cost & Task Time */}
                      <div className="border-t border-gray-100 pt-3">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-500">Internal Cost</label>
                            <div className="text-sm text-gray-900">{fmtCurrency(rate.internal_cost)}</div>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500">Task Time</label>
                            <div className="text-sm text-gray-900 flex items-center">
                              <ClockIcon className="h-4 w-4 text-gray-400 mr-1" />
                              {fmt(rate.task_time)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Products Card */}
            {products.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-[#009efb] to-[#0089db] px-6 py-3">
                  <div className="flex items-center">
                    <CubeIcon className="h-5 w-5 text-white mr-3" />
                    <h2 className="text-xl font-semibold text-white">Products / Materials ({products.length})</h2>
                  </div>
                </div>
                <div className="p-6">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Qty</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Regular Price</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Member Price</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Regular Total</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Member Total</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                 
                        {products.map((product, index) => (
                          <tr key={index}>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {product.product?.name || product.name || `Product #${product.id || index + 1}`}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">{fmt(product.qty)}</td>
                            <td className="px-4 py-3 text-sm text-gray-900">{fmtCurrency(product.regular_price)}</td>
                            <td className="px-4 py-3 text-sm text-gray-900">{fmtCurrency(product.member_price)}</td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">{fmtCurrency((parseFloat(product.regular_price) || 0) * (parseFloat(product.qty) || 0))}</td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">{fmtCurrency((parseFloat(product.member_price) || 0) * (parseFloat(product.qty) || 0))}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Overall Totals Card */}
            {(rates.length > 0 || products.length > 0) && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-[#009efb] to-[#0089db] px-6 py-3">
                  <div className="flex items-center">
                    <CurrencyDollarIcon className="h-5 w-5 text-white mr-3" />
                    <h2 className="text-xl font-semibold text-white">Overall Totals</h2>
                  </div>
                </div>
                <div className="p-6 space-y-6">
                  {/* Service Rates Totals */}
                  {rates.length > 0 && (
                    <div>
                      <h4 className="text-md font-medium text-gray-800 mb-3">Service Rates</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <div className="text-sm font-medium text-gray-700">Total – Regular Rate</div>
                          <div className="text-xl font-bold text-gray-900">${calcRatesTotal('regular_rate').toFixed(2)}</div>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <div className="text-sm font-medium text-gray-700">Total – Member Rate</div>
                          <div className="text-xl font-bold text-gray-900">${calcRatesTotal('member_rate').toFixed(2)}</div>
                        </div>
                        {hasAdditionalRates && (
                          <>
                            <div className="bg-gray-50 p-4 rounded-lg">
                              <div className="text-sm font-medium text-gray-700">Total – Add-On Rate</div>
                              <div className="text-xl font-bold text-gray-900">${calcRatesTotal('addon_rate').toFixed(2)}</div>
                            </div>
                            <div className="bg-gray-50 p-4 rounded-lg">
                              <div className="text-sm font-medium text-gray-700">Total – Premium Rate</div>
                              <div className="text-xl font-bold text-gray-900">${calcRatesTotal('premium_rate').toFixed(2)}</div>
                            </div>
                            <div className="bg-gray-50 p-4 rounded-lg">
                              <div className="text-sm font-medium text-gray-700">Total – Value Rate</div>
                              <div className="text-xl font-bold text-gray-900">${calcRatesTotal('value_rate').toFixed(2)}</div>
                            </div>
                            <div className="bg-gray-50 p-4 rounded-lg">
                              <div className="text-sm font-medium text-gray-700">Total – Afterhours Rate</div>
                              <div className="text-xl font-bold text-gray-900">${calcRatesTotal('after_hours_rate').toFixed(2)}</div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Product Totals */}
                  {products.length > 0 && (
                    <div>
                      <h4 className="text-md font-medium text-gray-800 mb-3">Products / Materials</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                          <div className="text-sm font-medium text-gray-700">Total – Regular Price</div>
                          <div className="text-xl font-bold text-blue-900">${calcProductsTotal('regular_price').toFixed(2)}</div>
                        </div>
                        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                          <div className="text-sm font-medium text-gray-700">Total – Member Price</div>
                          <div className="text-xl font-bold text-blue-900">${calcProductsTotal('member_price').toFixed(2)}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Grand Totals */}
                  <div className="border-t pt-4">
                    <h4 className="text-md font-medium text-gray-800 mb-3">Grand Totals</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                        <div className="text-sm font-medium text-gray-700">Grand Total – Regular</div>
                        <div className="text-xl font-bold text-green-900">
                          ${(calcRatesTotal('regular_rate') + calcProductsTotal('regular_price')).toFixed(2)}
                        </div>
                      </div>
                      <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                        <div className="text-sm font-medium text-gray-700">Grand Total – Member</div>
                        <div className="text-xl font-bold text-green-900">
                          ${(calcRatesTotal('member_rate') + calcProductsTotal('member_price')).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Portal Options Card */}
            {(portalOptions.is_available_in_portal || portalOptions.first_available_time || portalOptions.last_available_time) && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-[#009efb] to-[#0089db] px-6 py-3">
                  <div className="flex items-center">
                    <GlobeAltIcon className="h-5 w-5 text-white mr-3" />
                    <h2 className="text-xl font-semibold text-white">Web Portal Options</h2>
                  </div>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Available in Portal</label>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${portalOptions.is_available_in_portal ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {portalOptions.is_available_in_portal ? (<><CheckCircleIcon className="h-4 w-4 mr-1" />Yes</>) : (<><XCircleIcon className="h-4 w-4 mr-1" />No</>)}
                        </span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Arrival Time Window</label>
                      <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                        {portalOptions.arrival_time_window ? `${portalOptions.arrival_time_window} hour${portalOptions.arrival_time_window > 1 ? 's' : ''}` : 'N/A'}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">First Available Time</label>
                      <div className="p-3 bg-gray-50 rounded-lg text-gray-900 flex items-center">
                        <ClockIcon className="h-4 w-4 text-gray-400 mr-2" />
                        {fmt(portalOptions.first_available_time)}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Last Available Time</label>
                      <div className="p-3 bg-gray-50 rounded-lg text-gray-900 flex items-center">
                        <ClockIcon className="h-4 w-4 text-gray-400 mr-2" />
                        {fmt(portalOptions.last_available_time)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ─── Right Column ─── */}
          <div className="space-y-6">

            {/* Service Summary Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h2 className="text-lg font-semibold text-gray-900">Service Summary</h2>
              </div>
              <div className="p-6 space-y-4 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Status</span>
                  {getStatusBadge()}
                </div>
                {categoryName !== 'N/A' && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Category</span>
                    <span className="font-medium text-gray-900">{categoryName}</span>
                  </div>
                )}
                {service.code && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Code</span>
                    <span className="font-medium text-gray-900">{service.code}</span>
                  </div>
                )}
                {rates.length > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Rates</span>
                    <span className="font-medium text-gray-900">{rates.length} rate{rates.length !== 1 ? 's' : ''}</span>
                  </div>
                )}
                {products.length > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Products</span>
                    <span className="font-medium text-gray-900">{products.length} product{products.length !== 1 ? 's' : ''}</span>
                  </div>
                )}
                {service.formated_created_at && (
                  <div className="pt-3 border-t border-gray-200">
                    <div className="text-xs font-medium text-gray-500 mb-1">Created At</div>
                    <div className="text-sm text-gray-900">{service.formated_created_at}</div>
                  </div>
                )}
                {service.formated_updated_at && (
                  <div>
                    <div className="text-xs font-medium text-gray-500 mb-1">Last Updated</div>
                    <div className="text-sm text-gray-900">{service.formated_updated_at}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Images Card */}
            {displayImages.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                  <div className="flex items-center">
                    <PhotoIcon className="h-5 w-5 text-gray-600 mr-2" />
                    <h2 className="text-lg font-semibold text-gray-900">Images ({displayImages.length})</h2>
                  </div>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-2 gap-3">
                    {displayImages.map((image, index) => (
                      <a
                        key={index}
                        href={image.path || image.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block group"
                      >
                        <div className="relative overflow-hidden rounded-lg">
                          <img
                            src={image.path || image.url}
                            alt={`Service preview ${index + 1}`}
                            className="w-full h-24 object-cover rounded-lg group-hover:scale-105 transition-transform duration-200"
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition duration-200 rounded-lg" />
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceView;
